import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Printer, Trash2, Edit } from "lucide-react";
import { useStockRollStore } from "../../../store/stockrollStore";
import { RollPrintDialog } from "./RollPrintDialog";
import { StockRollDialog } from "./StockRollDialog";
import { confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import { useAuthStore } from "../../../store/authStore";
import { formatQuantity } from "../../../utils/format";

export const StockRollsList: React.FC = () => {
    const rollStore = useStockRollStore();
    const { user } = useAuthStore();
    const [rolls, setRolls] = useState<any[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedRolls, setSelectedRolls] = useState<any[]>([]);
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [editRoll, setEditRoll] = useState<any>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    
    // Pagination & Filter
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 50,
        page: 0,
    });
    const [globalFilter, setGlobalFilter] = useState("");
    const debounceRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        loadData();
    }, [lazyParams.page, lazyParams.rows]);

    const loadData = (search = globalFilter) => {
        setLoading(true);
        let filters: any = [];
        
        if (search) {
            filters.push({
                logic: "or",
                filters: [
                    { field: "barcode", operator: "contains", value: search },
                    { field: "rollCode", operator: "contains", value: search },
                    { field: "stock.name", operator: "contains", value: search }
                ]
            });
        }

        rollStore.fetchDataDynamic({
            filter: filters.length > 0 ? { logic: "and", filters } : undefined,
            pageIndex: lazyParams.page + 1,
            pageSize: lazyParams.rows
        }).then((res: any) => {
            setRolls(res.items || []);
            setTotalRecords(res.totalCount || 0);
        }).finally(() => {
            setLoading(false);
        });
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setGlobalFilter(e.target.value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setLazyParams(prev => ({ ...prev, first: 0, page: 0 }));
            loadData(e.target.value);
        }, 500);
    };

    const handleDelete = (id: number) => {
        confirmDialog({
            message: 'Bu topu silmek istediğinize emin misiniz?',
            header: 'Top Silme Onayı',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await rollStore.deleteItem(id);
                    toast.success("Kumaş topu silindi!");
                    loadData();
                } catch (error: any) {
                    toast.error(error.message || "Silinirken hata oluştu.");
                }
            }
        });
    };

    const actionBodyTemplate = (rowData: any) => {
        return (
            <div className="flex gap-2 justify-center">
                <Button 
                    icon={<Printer className="w-4 h-4" />} 
                    className="p-button-rounded p-button-info p-button-text" 
                    tooltip="Yazdır" 
                    onClick={() => {
                        setSelectedRolls([rowData]);
                        setIsPrintModalOpen(true);
                    }} 
                />
                <Button 
                    icon={<Edit className="w-4 h-4 text-orange-500" />} 
                    className="p-button-rounded p-button-warning p-button-text" 
                    tooltip="Düzenle" 
                    onClick={() => {
                        setEditRoll(rowData);
                        setIsEditModalOpen(true);
                    }} 
                />
                <Button 
                    icon={<Trash2 className="w-4 h-4 text-red-500" />} 
                    className="p-button-rounded p-button-danger p-button-text" 
                    tooltip="Sil" 
                    onClick={() => handleDelete(rowData.id)} 
                />
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-4 p-4 h-full bg-[var(--bg)] text-[var(--text)]">
            <div className="flex justify-between items-center bg-[var(--card)] p-4 rounded-xl shadow-sm border border-[var(--border)]">
                <h1 className="text-xl font-bold">Top (Rulo) Yönetimi</h1>
                <div className="flex gap-2">
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText 
                            value={globalFilter} 
                            onChange={handleSearch} 
                            placeholder="Barkod veya Ürün Ara..." 
                            className="w-full sm:w-64 bg-[var(--input-bg)]"
                        />
                    </span>
                    <Button 
                        label="Seçilenleri Yazdır" 
                        icon={<Printer className="w-4 h-4 mr-2" />} 
                        onClick={() => setIsPrintModalOpen(true)} 
                        disabled={!selectedRolls || selectedRolls.length === 0}
                        className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
                    />
                </div>
            </div>

            <div className="flex-1 bg-[var(--card)] rounded-xl shadow-sm border border-[var(--border)] overflow-hidden">
                <DataTable
                    value={rolls}
                    lazy
                    paginator
                    first={lazyParams.first}
                    rows={lazyParams.rows}
                    totalRecords={totalRecords}
                    onPage={(e: any) => setLazyParams(e)}
                    loading={loading}
                    selection={selectedRolls}
                    onSelectionChange={(e) => setSelectedRolls(e.value)}
                    dataKey="id"
                    className="p-datatable-sm"
                    emptyMessage="Top bulunamadı."
                    stripedRows
                >
                    <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>
                    <Column field="barcode" header="Barkod" body={(row) => <span className="font-bold">{row.barcode}</span>} />
                    <Column field="stock.name" header="Ürün" />
                    <Column field="variant.name" header="Varyant" body={(row) => row.variant?.name || '-'} />
                    <Column field="warehouse.name" header="Depo" />
                    <Column field="currentQuantity" header="Mevcut Miktar" body={(row) => formatQuantity(row.currentQuantity)} />
                    <Column field="initialQuantity" header="İlk Giriş Miktarı" body={(row) => formatQuantity(row.initialQuantity)} />
                    <Column field="createdAt" header="Oluşturulma Tarihi" body={(row) => row.createdAt ? new Date(row.createdAt).toLocaleDateString("tr-TR") : "-"} />
                    <Column body={actionBodyTemplate} style={{ width: '100px', textAlign: 'center' }} />
                </DataTable>
            </div>

            <RollPrintDialog 
                isOpen={isPrintModalOpen}
                onClose={() => setIsPrintModalOpen(false)}
                selectedRolls={selectedRolls}
            />

            <StockRollDialog 
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                roll={editRoll}
                onSave={loadData}
            />
        </div>
    );
};
